import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTickets } from '../contexts/TicketContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../translations';
import TicketDetailModal from '../components/modals/TicketDetailModal';
import { ticketsAPI } from '../api/api';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import useIsMobile from '../hooks/useIsMobile';
import ResponsiveContainer from '../components/ui/ResponsiveContainer';
import FilterPanel from '../components/ui/FilterPanel';
import FilterActions from '../components/ui/FilterActions';
import { Button } from '../components/ui/Button';

// Utility functions for styling using theme tokens
const getPriorityColor = (priority) => {
  let priorityText = priority;
  if (priority && typeof priority === 'object' && priority.priority_name) {
    priorityText = priority.priority_name;
  }

  if (!priorityText || typeof priorityText !== 'string') {
    return 'bg-background-secondary text-text-secondary border border-background-secondary dark:bg-dark-background-secondary/20 dark:text-dark-text-light dark:border-dark-border';
  }

  switch (priorityText.toLowerCase()) {
    case 'critical': return 'bg-error-50 text-error-600 border border-error-100 dark:bg-error-500/10 dark:text-error-500 dark:border-error-500/20';
    case 'high': return 'bg-warning-50 text-warning-600 border border-warning-100 dark:bg-warning-500/10 dark:text-warning-500 dark:border-warning-500/20';
    case 'medium': return 'bg-highlight-50 text-highlight-600 border border-highlight-100 dark:bg-highlight-500/10 dark:text-highlight-500 dark:border-highlight-500/20';
    case 'low': return 'bg-success-50 text-success-600 border border-success-100 dark:bg-success-500/10 dark:text-success-500 dark:border-success-500/20';
    default: return 'bg-background-secondary text-text-secondary border border-background-secondary dark:bg-dark-background-secondary/20 dark:text-dark-text-light dark:border-dark-border';
  }
};

const getStatusColor = (status) => {
  let statusText = status;
  if (status && typeof status === 'object' && status.status_name) {
    statusText = status.status_name;
  }

  if (!statusText || typeof statusText !== 'string') {
    return 'bg-background-secondary text-text-secondary border border-background-secondary dark:bg-dark-background-secondary/20';
  }

  switch (statusText.toLowerCase()) {
    case 'to do':
    case 'beklemede':
      return 'bg-background-secondary text-text-primary border border-background-secondary dark:bg-dark-background-secondary dark:text-dark-text-primary dark:border-dark-border';
    case 'in progress':
    case 'yapılıyor':
      return 'bg-highlight-50 text-highlight-600 border border-highlight-200 dark:bg-highlight-500/10 dark:text-highlight-500';
    case 'done':
    case 'bitti':
      return 'bg-success-50 text-success-600 border border-success-200 dark:bg-success-500/10 dark:text-success-500';
    default: return 'bg-background-secondary text-text-secondary border border-background-secondary dark:bg-dark-background-secondary/20';
  }
};

const getTypeIcon = (type) => {
  let typeText = type;
  if (type && typeof type === 'object' && type.type_name) {
    typeText = type.type_name;
  }

  if (!typeText || typeof typeText !== 'string') {
    return '📝';
  }

  switch (typeText.toLowerCase()) {
    case 'bug': return '🐛';
    case 'task': return '📋';
    case 'story': return '📖';
    case 'epic': return '🎯';
    default: return '📝';
  }
};

const TicketPage = () => {
  const { user, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const { tickets, loading, updateTicketDetails, setTickets } = useTickets();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('app_loading', { detail: pageLoading || loading }));
  }, [pageLoading, loading]);

  useEffect(() => {
    const loadTickets = async () => {
      try {
        setPageLoading(true);
        setError(null);
        // await ticketsAPI.debugTickets();
        const response = await ticketsAPI.getMyTickets();

        if (response.success) {
          setTickets(response.data);
        } else {
          setError(response.message);
        }
      } catch (error) {
        setError(t('errorOccurred') || 'Tickets yüklenirken bir hata oluştu');
      } finally {
        setPageLoading(false);
      }
    };

    if (isAuthenticated) {
      loadTickets();
    } else {
      setPageLoading(false);
    }
  }, [isAuthenticated, language]);

  useEffect(() => {
    const handleTicketCreated = () => {
      const loadTickets = async () => {
        try {
          const response = await ticketsAPI.getMyTickets();
          if (response.success) {
            setTickets(response.data);
          }
        } catch (error) {
          // Silently handle error
        }
      };
      loadTickets();
    };

    window.addEventListener('ticketCreated', handleTicketCreated);
    return () => window.removeEventListener('ticketCreated', handleTicketCreated);
  }, [setTickets]);

  if (error) {
    return (
      <div className="p-8 bg-background-main dark:bg-dark-background-alt min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary dark:text-dark-text-primary mb-2">
            {t('errorOccurred')}
          </h2>
          <p className="text-text-secondary dark:text-dark-text-secondary mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            {t('tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());

    let ticketStatus = ticket.status;
    if (ticket.status && typeof ticket.status === 'object' && ticket.status.status_name) {
      ticketStatus = ticket.status.status_name;
    }

    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const filterLower = statusFilter.toLowerCase();
      const statusLower = ticketStatus?.toLowerCase() || '';

      if (filterLower === 'beklemede') {
        matchesStatus = statusLower === 'beklemede' || statusLower === 'to do' || statusLower.includes('beklemede') || statusLower.includes('to do');
      } else if (filterLower === 'yapılıyor') {
        matchesStatus = statusLower === 'yapılıyor' || statusLower === 'in progress' || statusLower.includes('yapılıyor') || statusLower.includes('in progress');
      } else if (filterLower === 'bitti') {
        matchesStatus = statusLower === 'bitti' || statusLower === 'done' || statusLower.includes('bitti') || statusLower.includes('done');
      } else {
        matchesStatus = statusLower === filterLower || statusLower.includes(filterLower);
      }
    }

    let ticketPriority = ticket.priority;
    if (ticket.priority && typeof ticket.priority === 'object' && ticket.priority.priority_name) {
      ticketPriority = ticket.priority.priority_name;
    }
    const matchesPriority = priorityFilter === 'all' || ticketPriority?.toLowerCase() === priorityFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
  };

  const handleTicketUpdate = async (updatedTicket) => {
    try {
      updateTicketDetails(updatedTicket.id, updatedTicket);
      setShowTicketDetail(false);
      setSelectedTicket(null);
    } catch (error) {
      // Silent error
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  return (
    <div className="bg-background-main/30 dark:bg-dark-background-alt/30 min-h-screen">
      <ResponsiveContainer size="xl" className="py-3">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary dark:text-dark-text-primary mb-1">
            {t('myRequests')}
          </h1>
          <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
            {t('myRequestsSubtitle')}
          </p>
        </div>

        <FilterPanel
          title="Filters"
          isOpen={showFilters}
          onToggle={() => setShowFilters(!showFilters)}
          className="mb-6"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary dark:text-dark-text-light" />
                <input
                  type="text"
                  placeholder={t('searchTickets')}
                  className="w-full pl-10 pr-4 py-2 border border-background-secondary dark:border-dark-border rounded-lg focus:ring-2 focus:ring-highlight-500 focus:border-transparent bg-white dark:bg-dark-background-secondary text-text-primary dark:text-dark-text-primary"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="px-3 py-2 border border-background-secondary dark:border-dark-border rounded-lg focus:ring-2 focus:ring-highlight-500 focus:border-transparent bg-white dark:bg-dark-background-secondary text-text-primary dark:text-dark-text-primary transition-all"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('allStatuses')}</option>
                <option value="Beklemede">{t('pending')}</option>
                <option value="Yapılıyor">{t('inProgress')}</option>
                <option value="Bitti">{t('completed')}</option>
              </select>

              <select
                className="px-3 py-2 border border-background-secondary dark:border-dark-border rounded-lg focus:ring-2 focus:ring-highlight-500 focus:border-transparent bg-white dark:bg-dark-background-secondary text-text-primary dark:text-dark-text-primary transition-all"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="all">{t('allPriorities')}</option>
                <option value="Critical">{t('urgent')}</option>
                <option value="High">{t('high')}</option>
                <option value="Medium">{t('medium')}</option>
                <option value="Low">{t('low')}</option>
              </select>
            </div>

            <FilterActions onClear={handleClearFilters} />
          </div>
        </FilterPanel>

        {/* Tickets Table / Cards */}
        <div className="bg-white dark:bg-dark-background-main rounded-xl shadow-sm border border-background-secondary dark:border-dark-border overflow-hidden">
          {isMobile ? (
            <div className="divide-y divide-background-secondary dark:divide-dark-border">
              {filteredTickets.length === 0 ? (
                <div className="px-4 py-10 text-center bg-white dark:bg-dark-background-main">
                  <ExclamationTriangleIcon className="w-10 h-10 text-text-secondary/30 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-text-primary dark:text-dark-text-primary mb-1">
                    {t('noTasksFound')}
                  </h3>
                  <p className="text-xs text-text-secondary dark:text-dark-text-secondary">
                    {t('searchPlaceholder')}
                  </p>
                </div>
              ) : (
                filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => handleTicketClick(ticket)}
                    className="p-4 space-y-3 hover:bg-background-main/50 dark:hover:bg-dark-background-secondary/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getTypeIcon(ticket.ticket_type)}</span>
                          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary truncate">
                            {ticket.title}
                          </h3>
                        </div>
                        <p className="text-[10px] font-bold text-text-secondary/60 mt-1">#{ticket.id}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority?.priority_name || ticket.priority}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                        {ticket.status?.status_name || ticket.status}
                      </span>
                      <span className="text-[11px] text-text-secondary dark:text-dark-text-secondary">
                        {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US') : '-'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
          <>
          {/* Table Header */}
          <div className="px-6 py-4 bg-background-main/50 dark:bg-dark-background-secondary/50 border-b border-background-secondary dark:border-dark-border">
            <div className="grid grid-cols-12 gap-4 text-xs font-bold text-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
              <div className="col-span-4">{t('ticketTitle')}</div>
              <div className="col-span-2 text-center">{t('status')}</div>
              <div className="col-span-2 text-center">{t('priority')}</div>
              <div className="col-span-2">{t('assignee')}</div>
              <div className="col-span-2 text-right">{t('createdDate')}</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-background-secondary dark:divide-dark-border">
            {filteredTickets.length === 0 ? (
              <div className="px-6 py-12 text-center bg-white dark:bg-dark-background-main">
                <ExclamationTriangleIcon className="w-12 h-12 text-text-secondary/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary dark:text-dark-text-primary mb-1">
                  {t('noTasksFound')}
                </h3>
                <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                  {t('searchPlaceholder')}
                </p>
              </div>
            ) : (
              filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="px-6 py-4 hover:bg-background-main/50 dark:hover:bg-dark-background-secondary/30 cursor-pointer transition-all duration-200 group"
                  onClick={() => handleTicketClick(ticket)}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Title */}
                    <div className="col-span-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-xl filter grayscale group-hover:grayscale-0 transition-all">{getTypeIcon(ticket.ticket_type)}</span>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-text-primary dark:text-dark-text-primary truncate">
                            {ticket.title}
                          </span>
                          <span className="text-[10px] font-bold text-text-secondary/50 dark:text-dark-text-light/50 tracking-wider">
                            #{ticket.id}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-2 flex justify-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                        {ticket.status?.status_name || ticket.status}
                      </span>
                    </div>

                    {/* Priority */}
                    <div className="col-span-2 flex justify-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority?.priority_name || ticket.priority}
                      </span>
                    </div>

                    {/* Assignee */}
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm">
                          {ticket.assigned_to_user ?
                            (typeof ticket.assigned_to_user === 'string' ?
                              ticket.assigned_to_user.charAt(0) :
                              ticket.assigned_to_user.first_name?.charAt(0) || '?'
                            ) : '?'
                          }
                        </div>
                        <span className="text-[11px] font-medium text-text-primary dark:text-dark-text-primary truncate">
                          {ticket.assigned_to_user ?
                            (typeof ticket.assigned_to_user === 'string' ?
                              ticket.assigned_to_user :
                              `${ticket.assigned_to_user.first_name || ''} ${ticket.assigned_to_user.last_name || ''}`.trim() || t('unassigned')
                            ) : t('unassigned')
                          }
                        </span>
                      </div>
                    </div>

                    {/* Created Date */}
                    <div className="col-span-2 text-right">
                      <div className="flex flex-col">
                        <span className="text-[11px] font-medium text-text-secondary dark:text-dark-text-secondary">
                          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          </>
          )}
        </div>

        {/* Stats Footer */}
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-text-secondary dark:text-dark-text-light px-2">
          <span>
            {filteredTickets.length} / {tickets.length} {t('ticketsShown')}
          </span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1.5">
              <ClockIcon className="w-3.5 h-3.5" />
              <span>{t('lastUpdate')}: {new Date().toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}</span>
            </div>
          </div>
        </div>
      </ResponsiveContainer>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={showTicketDetail}
        onClose={() => {
          setShowTicketDetail(false);
          setSelectedTicket(null);
        }}
        onTicketChange={setSelectedTicket}
        onUpdate={handleTicketUpdate}
      />
    </div>
  );
};

export default TicketPage;